import {useLocation, useNavigate, useParams} from "react-router-dom";
import React, { useEffect, useState } from "react";
import AdminNav from "../../Components/AdminNav/AdminNav";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import { fetchSingleBrand, updateInternationalBrand } from "../../actions/International";
import { IMAGE_URL } from "../../path";

interface FormData {
    name: string;
    bw_logo: File | null;
    color_logo: File | null;
    cover_image: File | null;
    instagram_url: string;
    facebook_url: string;
    twitter_url: string;
    website_url: string;
    content: string;
}

function EditInternationalBrand() {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const location = useLocation();
    const user = location.state?.user;
    const { id } = useParams<{ id: string }>();

    const [formData, setFormData] = useState<FormData>({
        name: "",
        bw_logo: null,
        color_logo: null,
        cover_image: null,
        instagram_url: "",
        facebook_url: "",
        twitter_url: "",
        website_url: "",
        content: ""
    });

    const [imagePreview, setImagePreview] = useState<{
        bw_logo?: string | null;
        color_logo?: string | null;
        cover_image?: string | null;
    }>({});

    const image_url = IMAGE_URL;
    const navigate = useNavigate();

    const getBrand = async () => {
        try {
            const response = await fetchSingleBrand(Number(id));
            const data = response.data.data;

            setFormData({
                name: data.name || "",
                bw_logo: null,
                color_logo: null,
                cover_image: null,
                instagram_url: data.instagram_url || "",
                facebook_url: data.facebook_url || "",
                twitter_url: data.twitter_url || "",
                website_url: data.website_url || "",
                content: data.content || ""
            });

            setImagePreview({
                bw_logo: data.bw_logo ? image_url + data.bw_logo : null,
                color_logo: data.color_logo ? image_url + data.color_logo : null,
                cover_image: data.cover_image ? image_url + data.cover_image : null,
            });
        } catch (error) {
            console.error("Error fetching brand:", error);
        }
    };

    const ReactQuill = require('react-quill');
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
        ],
    };

    useEffect(() => {
        getBrand();
    }, [id]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, type, value } = e.target;

        if (type === "file") {
            const file = (e.target as HTMLInputElement).files?.[0] || null;

            setFormData(prev => ({ ...prev, [name]: file }));

            if (file) {
                const previewUrl = URL.createObjectURL(file);
                setImagePreview(prev => ({ ...prev, [name]: previewUrl }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            const formDataToSend = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                if (value instanceof File) {
                    formDataToSend.append(key, value);
                } else {
                    formDataToSend.append(key, value ?? "");
                }
            });

            const response = await updateInternationalBrand(Number(id), formDataToSend);

            if (response.status === 200) {
                alert("Brand successfully updated!");
                navigate("/dashboard/international/brands/list", { state: { user } });
            } else {
                alert(response.data?.message || "Unexpected error occurred");
            }
        } catch (error: any) {
            console.error("Update Error:", error);
            if (error.response?.data?.errors) {
                const messages = Object.values(error.response.data.errors).flat();
                alert(messages.join("\n"));
            } else {
                alert("Something went wrong submitting the form");
            }
        }
    };

    return (
        <div>
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <h2>Edit International Brand</h2>

                <form
                    onSubmit={handleSubmit}
                    encType="multipart/form-data"
                    className="form-horizontal form-label-left"
                    style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                >
                    {/* NAME */}
                    <input
                        type="text"
                        className="input-email"
                        placeholder="Brand Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                    />

                    <label>Brand Content</label>
                    <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={(value: any) => setFormData(prev => ({ ...prev, content:value }))}
                        modules={quillModules}
                        style={{ background:"#fff", minHeight:"200px", marginBottom:"20px" }}
                    />

                    {/* Logos */}
                    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                        {["bw_logo", "color_logo"].map(key => (
                            <div key={key} style={{ display: "flex", flexDirection: "column", flex: 1, alignItems: "center" }}>
                                <label>{key === "bw_logo" ? "Black & White Logo" : "Color Logo"}</label>
                                <input type="file" name={key} accept="image/*" onChange={handleInputChange} />
                                {imagePreview[key as keyof typeof imagePreview] && (
                                    <img src={imagePreview[key as keyof typeof imagePreview]!} style={{ width: "100%", maxWidth: "200px", marginTop: "8px" }} />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Cover Image */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <label>Cover Image</label>
                        <input type="file" name="cover_image" accept="image/*" onChange={handleInputChange} />
                        {imagePreview.cover_image && (
                            <img src={imagePreview.cover_image} style={{ maxWidth: "1000px", marginTop: "8px" }} />
                        )}
                    </div>

                    {/* Social Links */}
                    {["instagram_url", "facebook_url", "twitter_url", "website_url"].map(key => (
                        <input
                            key={key}
                            type="text"
                            name={key}
                            placeholder={key.replace("_", " ").toUpperCase()}
                            className="input-email"
                            value={formData[key as keyof FormData] as string} // cast to string for TS
                            onChange={handleInputChange}
                        />
                    ))}

                    <button type="submit" className="btn btn-success">Update</button>
                </form>
            </div>
        </div>
    );
}

export default EditInternationalBrand;
